import React, { useEffect, useRef, useState } from 'react';
import './css/Projects.css';

const projects = [
  {
    id: 'mindle',
    index: '01',
    codename: 'MINDLE',
    tagline: 'AI-Driven Mood Intelligence Platform',
    category: 'AI System · Production Engineering',
    status: 'ACTIVE',
    accent: '#998498',
    accentRgb: '109,86,255',
    description:
      'AI-powered sentiment analysis system designed with production-grade backend architecture and observability practices.',
    highlights: [
      'Real-time sentiment analysis with user feedback validation',
      'Model accuracy tracking and response latency monitoring',
      'Structured logging pipeline for prediction tracing',
      'Metrics dashboard exposing request volume, error rates, mood distribution',
      'Backend API design with scalable request handling',
    ],
    metrics: [
      
    ],
    stack: ['React', 'Node.js', 'Express', 'TypeScript', 'REST APIs', 'WebSockets', 'Dexie', 'Antdesign', 'GraphQL', 'MongoDB', 'Apollo'],
    links: [
      {
        icon: 'git',
        label: 'Client Code',
        link: 'https://github.com/sj056/Mindle-client'
      },
      {
        icon: 'git',
        label: 'Server Code',
        link: 'https://github.com/sj056/Mindle-server'
      },
      {
        icon: 'link',
        label: 'Deployed link',
        link: 'https://mindle-client.vercel.app/'
      }
    ]
  },
  {
    id: 'hydrarate',
    index: '02',
    codename: 'HYDRARATE',
    tagline: 'Distributed Token Bucket Rate Limiting Library',
    category: 'Infrastructure · Distributed Systems',
    status: 'PUBLISHED',
    accent: '#6ca0ad',
    accentRgb: '0,201,167',
    description:
      'Production-grade rate-limiting infrastructure built around the Token Bucket algorithm with distributed state management.',
    highlights: [
      'Custom Token Bucket algorithm — tokens = min(capacity, tokens + elapsed × refillRate)',
      'Redis-backed distributed buckets with atomic Lua operations',
      'Pluggable Express middleware integration',
      'Prometheus-style metrics exposure for observability',
      'Standardized rate-limit headers & retry policies',
      'Clean architecture using store abstraction pattern',
    ],
    stack: ['Node.js', 'Express', 'TypeScript', 'Redis', 'Lua Scripting', 'Prometheus Metrics', 'Middleware Patterns'],
    links: [
      {
        icon: 'git',
        label: 'Code',
        link: 'https://github.com/sj056/hydrarate'
      }
    ]
  },
];

/* ── Animated number counter ── */
const Counter = ({ target, suffix = '', isFloat = false }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const end = parseFloat(target);
      const dur = 1400;
      const steps = 50;
      const inc = end / steps;
      let cur = 0;
      const t = setInterval(() => {
        cur += inc;
        if (cur >= end) { setVal(end); clearInterval(t); }
        else setVal(isFloat ? parseFloat(cur.toFixed(1)) : Math.floor(cur));
      }, dur / steps);
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);

  return <span ref={ref}>{val}{suffix}</span>;
};

/* ── Scrolling data stream bar ── */
const DataStream = ({ color }) => {
  const chars = '01アイウ∑∆◈⬡░▒▓';
  const stream = Array.from({ length: 32 }, () =>
    chars[Math.floor(Math.random() * chars.length)]
  ).join(' ');
  return (
    <div className="proj-stream-wrap">
      <div className="proj-stream" style={{ '--stream-color': color }}>
        {stream} · {stream}
      </div>
    </div>
  );
};

const Projects = () => {
  const [visible, setVisible] = useState({});
  const cardRefs = useRef([]);

  useEffect(() => {
    cardRefs.current.forEach((el, i) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) setVisible(p => ({ ...p, [i]: true })); },
        { threshold: 0.15 }
      );
      obs.observe(el);
      return () => obs.disconnect();
    });
  }, []);

  return (
    <>


      <section className="projects-section" id="projects">

        <h3 className='m-20'>Projects</h3>

        <div className="projects-label">
          Selected Work
        </div>

        <div className="projects-grid">
          {projects.map((p, i) => (
            <div
              key={p.id}
              ref={el => (cardRefs.current[i] = el)}
              className={`proj-card ${visible[i] ? 'visible' : ''}`}
              style={{
                '--proj-accent': p.accent,
                '--proj-rgb': p.accentRgb,
                transitionDelay: `${i * 0.15}s`,
              }}
            >
              {/* Top bar */}
              <div className="proj-topbar">
                <span className="proj-index">PROJECT_{p.index}</span>
                <span className="proj-status">
                  <span className="proj-status-dot" />
                  {p.status}
                </span>
              </div>

              {/* Body */}
              <div className="proj-body">

                {/* Left */}
                <div className="proj-left">
                  <div className="proj-category">{p.category}</div>
                  <h3 className="proj-name">{p.codename}</h3>
                  <p className="proj-tagline">// {p.tagline}</p>
                  <p className="proj-desc">{p.description}</p>

                  <ul className="proj-highlights">
                    {p.highlights.map((h, j) => (
                      <li key={j} className="proj-highlight">{h}</li>
                    ))}
                  </ul>

                  <div className="proj-stack">
                    {p.stack.map((s, j) => (
                      <span key={j} className="proj-pill">{s}</span>
                    ))}
                  </div>

                  {
                    p.links.map((val, ind) => {
                      return (
                        <a href={val.link} target="_blank" rel="noreferrer" className="proj-github">
                          {val.icon === 'git' ?
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
                            </svg>
                            :
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" />
                              <line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          }
                          {val.label}
                        </a>
                      )
                    })
                  }
                </div>

                {/* Right — metrics */}
                {/* <div className="proj-right">
                  <div className="proj-metrics-label">// sys.metrics</div>
                  {p.metrics.map((m, j) => {
                    const numericVal = parseFloat(m.value.replace(/[^0-9.]/g, ''));
                    const barPct = m.unit === '%'
                      ? `${numericVal}%`
                      : m.unit === 'ms' || m.unit === 'p99'
                        ? '70%'
                        : m.unit === 'suites' ? '60%' : '80%';

                    return (
                      <div key={j} className="proj-metric">
                        <div className="proj-metric-val">
                          {m.value.startsWith('<') ? (
                            <span>{m.value}</span>
                          ) : (
                            <Counter
                              target={numericVal}
                              isFloat={m.value.includes('.')}
                            />
                          )}
                          <span className="proj-metric-unit">{m.unit}</span>
                        </div>
                        <div className="proj-metric-label">{m.label}</div>
                        <div className="proj-metric-bar">
                          <div
                            className="proj-metric-bar-fill"
                            style={{ '--bar-w': barPct }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div> */}
              </div>

              {/* Scrolling data stream */}
              <DataStream color={p.accent} />
            </div>
          ))}
        </div>

      </section>
    </>
  );
};

export default Projects;